class AZKeywords {
  constructor() {
    this.parser = new DOMParser();
    this.init();
  }

  init() {
    this.container = document.querySelector('[data-keywords-page]');
    if (!this.container) return;

    // Determine which page we're on
    this.pageType = this.container.querySelector('[data-metaobject-type]')?.getAttribute('data-metaobject-type') || 'keywords';
    
    this.searchInput = document.getElementById('keyword-search');
    this.sections = document.querySelectorAll('.az-letter-section');
    this.letterLinks = document.querySelectorAll('.az-letter-link');
    
    this.currentPage = 1;
    this.isLoading = false;
    this.hasMorePages = document.querySelector('[data-has-more-pages="true"]') !== null;
    
    this.cache = new Map();
    this.cacheKey = `az_${this.pageType}_cache`; // Unique cache key per page type
    this.cacheExpiry = `az_${this.pageType}_expiry`; // Unique expiry key per page type
    
    // Check cache first, then load data
    if (this.getFromCache()) {
      this.renderFromCache();
    } else {
      this.container.insertAdjacentHTML('afterbegin', '<div class="loading-overlay" style="text-align: center; padding: 2rem 0">Loading all keywords...</div>');
      this.loadAllPages();
    }
    
    this.setupEventListeners();
    this.setupIntersectionObserver();
    // Initialize pagination observer after other setup
    this.setupPaginationObserver();
  }

  createKeywordElement(keywordData) {
    const li = document.createElement('li');
    li.className = 'az-keyword-item';
    
    const link = document.createElement('a');
    // Handle different URL structures based on page type
    if (this.pageType === 'influencers') {
      link.href = keywordData.url || '#';
    } else {
      link.href = keywordData.url;
    }
    link.className = 'az-keyword-link';
    link.setAttribute('data-keyword', keywordData.text);
    link.textContent = keywordData.text;
    
    li.appendChild(link);
    return li;
  }

  updateEmptySections() {
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
    
    letters.forEach(letter => {
      const section = document.querySelector(`.section-${letter}`);
      const letterLink = document.querySelector(`[data-letter="${letter}"]`);
      
      if (section) {
        const hasKeywords = section.querySelector('.az-keyword-item') !== null;
        section.style.display = hasKeywords ? '' : 'none';
        
        if (letterLink) {
          letterLink.classList.toggle('disabled', !hasKeywords);
          letterLink.setAttribute('aria-disabled', !hasKeywords);
        }
      }
    });
  }

  setupEventListeners() {
    // Search functionality
    if (this.searchInput) {
      this.searchInput.addEventListener('input', this.debounce((e) => {
        this.handleSearch(e.target.value);
      }, 300));
    }

    // Letter navigation
    this.letterLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        if (link.classList.contains('disabled')) return;
        
        const targetId = link.getAttribute('href');
        const targetSection = document.querySelector(targetId);
        if (targetSection && !targetSection.style.display) {
          targetSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  }

  handleSearch(searchTerm) {
    searchTerm = searchTerm.toLowerCase().trim();
    
    this.sections.forEach(section => {
      let hasVisibleItems = false;
      const items = section.querySelectorAll('.az-keyword-item');
      
      items.forEach(item => {
        const keyword = item.querySelector('.az-keyword-link').getAttribute('data-keyword').toLowerCase();
        const isVisible = searchTerm === '' || keyword.includes(searchTerm);
        item.style.display = isVisible ? '' : 'none';
        if (isVisible) hasVisibleItems = true;
      });
      
      section.style.display = hasVisibleItems ? '' : 'none';
    });
  }

  setupIntersectionObserver() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const letter = entry.target.id.split('-')[1];
          this.updateActiveLetter(letter);
        }
      });
    }, {
      rootMargin: '-10% 0px -80% 0px'
    });

    this.sections.forEach(section => observer.observe(section));
  }

  updateActiveLetter(activeLetter) {
    this.letterLinks.forEach(link => {
      const letter = link.getAttribute('data-letter');
      link.classList.toggle('active', letter === activeLetter);
    });
  }

  setupPaginationObserver() {
    if (!this.hasMorePages) return;
    
    const paginationTrigger = document.querySelector('.pagination-trigger');
    if (!paginationTrigger) return;
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.isLoading && this.hasMorePages) {
          this.loadNextPage();
        }
      });
    }, {
      rootMargin: '0px 0px 200px 0px'
    });
    
    observer.observe(paginationTrigger);
  }

  insertInAlphabeticalOrder(parentList, newItem) {
    const newKeyword = newItem.querySelector('.az-keyword-link').getAttribute('data-keyword').toLowerCase();
    const existingItems = parentList.querySelectorAll('.az-keyword-item');
    
    let insertPosition = null;
    
    for (const item of existingItems) {
      const itemKeyword = item.querySelector('.az-keyword-link').getAttribute('data-keyword').toLowerCase();
      if (newKeyword < itemKeyword) {
        insertPosition = item;
        break;
      }
    }
    
    if (insertPosition) {
      parentList.insertBefore(newItem, insertPosition);
    } else {
      parentList.appendChild(newItem);
    }
  }

  debounce(func, wait) {
    let timeout;
    return function(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  getFromCache() {
    try {
      const expiry = localStorage.getItem(this.cacheExpiry);
      if (!expiry || Date.now() > parseInt(expiry)) {
        // Clear expired or invalid cache
        localStorage.removeItem(this.cacheKey);
        localStorage.removeItem(this.cacheExpiry);
        return false;
      }
      
      const cached = localStorage.getItem(this.cacheKey);
      if (cached) {
        const parsedCache = JSON.parse(cached);
        // Check if cache is empty or invalid
        if (!parsedCache || !Array.isArray(parsedCache) || parsedCache.length === 0) {
          localStorage.removeItem(this.cacheKey);
          localStorage.removeItem(this.cacheExpiry);
          return false;
        }
        this.cache = new Map(parsedCache);
        return true;
      }
    } catch (e) {
      console.warn('Cache read error:', e);
      // Clear invalid cache
      localStorage.removeItem(this.cacheKey);
      localStorage.removeItem(this.cacheExpiry);
    }
    return false;
  }

  saveToCache() {
    try {
      // Only save if cache has valid data
      if (this.cache.size > 0) {
        const cacheData = [...this.cache];
        // Verify we have actual keywords in the cache
        const hasKeywords = cacheData.some(([_, keywords]) => 
          Array.isArray(keywords) && keywords.length > 0
        );
        
        if (hasKeywords) {
          localStorage.setItem(this.cacheKey, JSON.stringify(cacheData));
          localStorage.setItem(this.cacheExpiry, (Date.now() + 600000).toString()); // 10 minutes (600000ms)
        } else {
          // Clear empty cache
          localStorage.removeItem(this.cacheKey);
          localStorage.removeItem(this.cacheExpiry);
        }
      }
    } catch (e) {
      console.warn('Cache write error:', e);
      // Clear potentially corrupted cache
      localStorage.removeItem(this.cacheKey);
      localStorage.removeItem(this.cacheExpiry);
    }
  }

  renderFromCache() {
    // Verify cache has content before rendering
    if (this.cache.size === 0) {
      this.loadAllPages();
      return;
    }

    let hasValidContent = false;
    this.cache.forEach((keywords, letter) => {
      if (Array.isArray(keywords) && keywords.length > 0) {
        const section = document.querySelector(`.section-${letter} .az-keywords-list`);
        if (section) {
          keywords.forEach(keyword => {
            const element = this.createKeywordElement(keyword);
            this.insertInAlphabeticalOrder(section, element);
          });
          hasValidContent = true;
        }
      }
    });

    // If no valid content was rendered, load fresh data
    if (!hasValidContent) {
      this.loadAllPages();
      return;
    }

    this.updateEmptySections();
  }

  async loadNextPage(retryCount = 0) {
    if (this.isLoading) return false;

    try {
      const paginationTrigger = document.querySelector('.pagination-trigger');
      const nextUrl = paginationTrigger?.getAttribute('data-next-url');

      if (!nextUrl || !paginationTrigger) {
        this.hasMorePages = false;
        return false;
      }

      this.isLoading = true;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(nextUrl, {
        signal: controller.signal,
        headers: {
          'Accept': 'text/html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      clearTimeout(timeoutId);

      const html = await response.text();
      const doc = this.parser.parseFromString(html, 'text/html');
      const newKeywords = doc.querySelectorAll('.az-keyword-item');

      // Process and cache keywords
      newKeywords.forEach(keyword => {
        const link = keyword.querySelector('.az-keyword-link');
        if (!link) return;

        const keywordText = link.getAttribute('data-keyword');
        const firstChar = keywordText.charAt(0).toUpperCase();
        
        if (!this.cache.has(firstChar)) {
          this.cache.set(firstChar, []);
        }
        
        const keywordData = {
          text: keywordText,
          url: link.href,
          type: this.pageType
        };
        
        if (!this.cache.get(firstChar).some(k => k.text === keywordText)) {
          this.cache.get(firstChar).push(keywordData);
          const clone = this.createKeywordElement(keywordData);
          const section = document.querySelector(`.section-${firstChar} .az-keywords-list`);
          if (section) {
            this.insertInAlphabeticalOrder(section, clone);
          }
        }
      });

      // Update pagination
      const newPaginationTrigger = doc.querySelector('.pagination-trigger');
      if (newPaginationTrigger) {
        const newNextUrl = newPaginationTrigger.getAttribute('data-next-url');
        if (newNextUrl) {
          paginationTrigger.setAttribute('data-next-url', newNextUrl);
          this.hasMorePages = true;
        } else {
          this.hasMorePages = false;
          paginationTrigger.remove();
        }
      } else {
        this.hasMorePages = false;
        paginationTrigger.remove();
      }

      return true;

    } catch (error) {
      if (retryCount < 2) {
        console.warn('Retrying due to error. Retry no:', retryCount);
        await new Promise(resolve => setTimeout(resolve, 1000));
        return this.loadNextPage(retryCount + 1);
      }
      if (error.name === 'AbortError') {
        console.warn('Request timed out; retrying...', retryCount);
      } else {
        console.error('Error loading next page:', error);
      }
      return false;
    } finally {
      this.isLoading = false;
    }
  }

  async loadAllPages() {
    if (this.isLoading) return;
    
    try {
      while (this.hasMorePages) {
        await this.loadNextPage();
        await new Promise(resolve => setTimeout(resolve, 300)); // Add small delay between requests
      }
    } catch (error) {
      console.error('Critical loading error:', error);
    } finally {
      this.saveToCache();
      this.updateEmptySections();
      document.querySelector('.loading-overlay')?.remove();
    }
  }
}

// Initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new AZKeywords());
} else {
  new AZKeywords();
}